"use client";

import Image from "next/image";
import React, { useRef, useEffect, useState } from "react";
import Coral4kDigitalAsset from "@/assets/images/decorative/objects/Coral4kDigitalAsset.webp";
import Level83Screenshot from "@/assets/images/new/old_way_vs_cove/cove_way.webp";
import VaultDecoration from "@/assets/images/decorative/objects/Vault_2.webp";
import BananaTreeDecoration from "@/assets/images/decorative/trees/bananatree.webp";
import NewsBoardDecoration from "@/assets/images/decorative/objects/NewsBoard.webp";
import { icons } from "@/icons";
import { data } from "@/data";
import styles from "./ComparisonSection.module.css";

const coveDecorations = [VaultDecoration, BananaTreeDecoration, NewsBoardDecoration];

// Mock boring banking app UI component
function BoringBankingApp() {
  return (
    <div className={styles.boringApp}>
      {/* Header */}
      <div className={styles.boringHeader}>
        <span className={styles.boringLogo}>BANK CO.</span>
        <div className={styles.boringAvatar} />
      </div>

      {/* Accounts Section */}
      <div className={styles.boringSection}>
        <div className={styles.boringSectionTitle}>Your Accounts</div>

        <div className={styles.boringAccount}>
          <div className={styles.boringAccountName}>Checking</div>
          <div className={styles.boringAccountBalanceNegative}>-$24.50</div>
        </div>

        <div className={styles.boringAccount}>
          <div className={styles.boringAccountName}>Savings</div>
          <div className={styles.boringAccountBalance}>$12.33</div>
        </div>

        <div className={styles.boringAccount}>
          <div className={styles.boringAccountName}>Investment</div>
          <div className={styles.boringAccountBalanceZero}>$0.00</div>
        </div>
      </div>

      {/* Overdraft notice */}
      <div className={styles.boringNotice}>
        <div className={styles.boringNoticeIcon}>!</div>
        <div className={styles.boringNoticeContent}>
          <p className={styles.boringNoticeTitle}>Overdraft Fee</p>
          <p className={styles.boringNoticeAmount}>-$35.00</p>
        </div>
      </div>
    </div>
  );
}

export default function ComparisonSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="beforeaftersection" className={styles.section}>
      {/* Header */}
      <header className={`${styles.header} ${isVisible ? styles.animateFadeInUp : ""}`}>
        <Image
          src={Coral4kDigitalAsset}
          alt="Coral decoration"
          width={120}
          height={120}
          className={styles.coralImage}
        />
        <h2 className={styles.headline}>{data.beforeaftersection.heading}</h2>
        <p className={styles.subheadline}>{data.beforeaftersection.subheading}</p>
      </header>

      {/* Main Comparison Grid */}
      <div className={styles.comparisonGrid}>
        {/* Normie Section */}
        <div className={styles.comparisonSection}>
          {/* Normie Phone */}
          <div
            className={`${styles.phoneShowcase} ${
              isVisible ? `${styles.animateFadeInUp} ${styles.delay1}` : ""
            }`}
          >
            <span className={`${styles.phoneLabel} ${styles.phoneLabelNormie}`}>
              The Old Way
            </span>
            <div className={`${styles.phoneFrame} ${styles.phoneFrameNormie}`}>
              <div className={styles.phoneScreen}>
                <div className={styles.phoneDynamicIsland} />
                <BoringBankingApp />
              </div>
            </div>
            <p className={`${styles.phoneName} ${styles.phoneNameNormie}`}>Normie</p>
          </div>

          {/* Normie Feature List */}
          <div className={`${styles.featureList} ${styles.featureListNormie}`}>
            {data.beforeaftersection.beforelist.map((item, index) => (
              <div
                key={index}
                className={`${styles.featureItem} ${
                  isVisible ? `${styles.animateFadeInLeft} ${styles[`delay${index + 2}`]}` : ""
                }`}
              >
                <div className={`${styles.featureIcon} ${styles.featureIconNormie}`}>
                  <icons.vectior />
                </div>
                <p className={`${styles.featureText} ${styles.featureTextNormie}`}>{item}</p>
              </div>
            ))}
          </div>
        </div>

        {/* VS Badge */}
        <div
          className={`${styles.vsBadge} ${
            isVisible ? `${styles.animateScaleIn} ${styles.delay3}` : ""
          }`}
        >
          <div className={styles.vsBadgeCircle}>
            <span className={styles.vsBadgeText}>VS</span>
          </div>
        </div>

        {/* Cove Section */}
        <div className={styles.comparisonSection}>
          {/* Cove Phone */}
          <div
            className={`${styles.phoneShowcase} ${
              isVisible ? `${styles.animateFadeInUp} ${styles.delay2}` : ""
            }`}
          >
            <span className={`${styles.phoneLabel} ${styles.phoneLabelCove}`}>
              The Cove Way
            </span>
            <div className={styles.phoneFrame}>
              <div className={`${styles.phoneScreen} ${styles.phoneScreenCove}`}>
                <div className={styles.phoneDynamicIsland} />
                <Image
                  src={Level83Screenshot}
                  alt="Cove app showing a beautiful island"
                  className={styles.coveScreenshot}
                  fill
                  sizes="(max-width: 768px) 80vw, 280px"
                  priority
                  placeholder="blur"
                />
              </div>
            </div>
            <p className={`${styles.phoneName} ${styles.phoneNameCove}`}>Coveling</p>
          </div>

          {/* Cove Feature List */}
          <div className={`${styles.featureList} ${styles.featureListCove}`}>
            {data.beforeaftersection.afterlist.map((item, index) => (
              <div
                key={index}
                className={`${styles.featureItem} ${styles.featureItemCove} ${
                  isVisible ? `${styles.animateFadeInRight} ${styles[`delay${index + 3}`]}` : ""
                }`}
              >
                <div className={styles.featureDecoration}>
                  <Image
                    src={coveDecorations[index]}
                    alt=""
                    width={48}
                    height={48}
                    className={styles.decorationImage}
                  />
                </div>
                <p className={`${styles.featureText} ${styles.featureTextCove}`}>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
